import { GiftCard } from '@vend/types'
import uuid from '@vend/vend-uuid'
import sortBy from 'lodash/sortBy'
import { add, round, vn } from 'vend-number'

import { ProviderSettings } from '../../components/gift-cards/provider-settings'
import * as WR_GIFT_CARDS from '../../constants'
import { dateFormat } from '../../filters/wr-date-format'
import { $templateCache } from '../../ng-compat/imports'

interface ValueInputOptions {
  min: number
  decimalPlaces: number
  allowedPrefix: string
  max: number | undefined
}

interface GiftCardLookup {
  code: string
}

export interface GiftCardSettings {
  minValue: number
  maxValue: number | undefined
}

/**
 * AbstractGiftCardsController defines an abstract implementation for the Gift Cards modal
 * which concrete Gift Card providers can implement.
 */
export abstract class AbstractGiftCardsController {
  /**
   * Retrieves a gift card and assigns it to the giftCard property.
   */
  public abstract retrieveGiftCard(): void
  /**
   * Makes a payment against the current giftCard.
   */
  public abstract payWithGiftCard(): void
  /**
   * Returns the text to display as the title of the search modal screen.
   */
  public abstract getSearchModalTitle(): string
  /**
   * Returns the text to display as the lead information of the search modal screen.
   */
  public abstract getSearchModalText(): string
  /**
   * Returns true if the gift card can be recharged, false otherwise.
   */
  public abstract isRechargeable(): boolean
  /**
   * Returns the display status of the gift card.
   */
  public abstract getGiftCardDisplayStatus(): string
  /**
   * Modal dismissible setting.
   */
  public dismissible = true

  /**
   * GiftCard is the model used for both searching for a Gift Card and also
   * is stored and used when displaying information about a Gift Card.
   */
  public giftCard: GiftCard | GiftCardLookup = {
    code: '',
  }

  /**
   * The merchant's gift card settings.
   */
  public giftCardSettings: GiftCardSettings = {
    minValue: 0,
    maxValue: undefined,
  }

  /**
   * A retrieved Gift Card's transactions are stored here for display.
   */
  public giftCardTransactions: any

  /**
   * Used when adding to an existing balance, this is the "after top up" balance.
   */
  public potentialBalance: any

  /**
   * The amount that will be added when adding to an existing Gift Card.
   */
  public reloadBalance: any

  /**
   * The workflow stream state. Identifies where we are in the current workflow.
   */
  public state: string

  /**
   * Waiting is set to true when a command is being processed, e.g. searching for a Gift Card.
   */
  public waiting = false

  constructor(
    /**
     * The settings the Gift Card provider has.
     */
    public providerSettings: ProviderSettings,
    /**
     * Modal close method.
     */
    public close: (args: any) => any,
    /**
     * When redeeming, the amount to be redeemed.
     */
    public amountTendered: number,
    /**
     * Regexp used for validating any entered Gift Card Code.
     */
    public giftCardCodeRegex: RegExp,
    /**
     * Min Characters required for the Gift Card Code.
     */
    public giftCardCodeMinChars: number,
    /**
     * Max Characters allowed for the Gift Card Code.
     */
    public giftCardCodeMaxChars: number,
    /**
     * The workflow stream, either selling or redeeming
     */
    public workflow: string,
    /**
     * The current active outlet.
     */
    public outlet: any,
    /**
     * Vend Number configuration options for gift card form inputs.
     */
    public valueInputOptions: ValueInputOptions
  ) {
    this.state = this.workflow

    const templates: { readonly [index: string]: string } = {
      ...WR_GIFT_CARDS.templates,
      ...WR_GIFT_CARDS.components,
    }

    for (const state of Object.keys(templates)) {
      $templateCache.put(this.getTemplateUrl(state), templates[state])
    }
  }

  /**
   * Determine if we are in a selling workflow.
   *
   */
  public isSellingWorkflow(): boolean {
    return this.workflow === WR_GIFT_CARDS.workflows.SELLING
  }

  /**
   * Returns a "fake" template URL for use with the template cache.
   */
  protected getTemplateUrl(state: string): string {
    return `GIFT_CARD_${state}`
  }

  /**
   * Helper to display the correct template based on current workflow and workflow state.
   */
  public getTemplate(): string {
    return this.getTemplateUrl(this.state)
  }

  public getValueForm(): any {
    const formName = `COMPONENT_${this.state}_FORM_LEGACY`
    return this.getTemplateUrl(formName)
  }

  /**
   * Reset current modal workflow state and the details of current gift card.
   */
  public resetStateAndGiftCard() {
    this.giftCard = {
      code: '',
    }
    this.giftCardTransactions = null
    this.state = this.workflow
  }

  /**
   * Move to the add balance state + modal
   */
  public addBalance() {
    this.state = WR_GIFT_CARDS.states.ADD_BALANCE
    this.giftCardTransactions = null
  }

  /**
   * Close the reload gift card modal and add the outstanding balance to the sale as a gift card.
   */
  public addGiftCardReloadToSale() {
    this.close({
      code: (this.giftCard as GiftCard).code,
      value: this.reloadBalance,
      isReload: true,
      clientId: uuid(),
      expiresAt: (this.giftCard as GiftCard).expiresAt,
    })
  }

  /**
   * Updates the live sum of the gift cards current balance and the balance to be added.
   *
   */
  public updatePotentialBalance() {
    this.reloadBalance = this.reloadBalance || vn(0)
    const newPotentialBalance = add(
      this.reloadBalance,
      (this.giftCard as GiftCard).balance
    )
    this.potentialBalance = round(newPotentialBalance, 2)
  }

  /**
   * Retrieve the formatted gift card expiry date, or 'None' if it doesn't have one.
   */
  public getGiftCardExpiryDate(): string {
    if ((this.giftCard as GiftCard).expiresAt) {
      return dateFormat(
        (this.giftCard as GiftCard).expiresAt,
        'MMM DD, YYYY',
        this.outlet.time_zone
      )
    }
    return 'None'
  }

  /**
   * Tells us if we should be showing the expiry date for a gift card
   */
  public shouldShowGiftCardExpiry(): boolean {
    return !!(this.giftCard as GiftCard).expiresAt
  }

  /**
   * The payment amount that the gift card can be used for. If the gift card balance is less than the payment amount,
   * the payable amount will be the remaining balance on the gift card, otherwise it will be the remaining payment
   * amount.
   */
  public getPayableAmount(): number {
    return this.isBalanceSufficient()
      ? this.amountTendered
      : (this.giftCard as GiftCard).balance
  }

  /**
   * Determine if the gift card has any balance remaining on it.
   */
  public hasBalance(): boolean {
    return vn((this.giftCard as GiftCard).balance).gt(0)
  }

  /**
   * Determine if the gift card balance is sufficient to cover the payment amount
   */
  public isBalanceSufficient(): boolean {
    return vn((this.giftCard as GiftCard).balance).gte(this.amountTendered)
  }

  /**
   * Ensures transactions are in the correct order and calculates a balance at the time of the transaction.
   */
  protected calculateTransactionsBalance(transactions: any) {
    this.giftCardTransactions = sortBy(transactions, 'created_at')

    this.giftCardTransactions.forEach((transaction: any, index: number) => {
      if (index === 0) {
        // First transaction is the gift card's activation
        transaction.balance = transaction.amount
      } else {
        transaction.balance = add(
          this.giftCardTransactions[index - 1].balance,
          transaction.amount
        )
      }
    })
  }

  public getGiftCardCodeMinChars(): number {
    return this.giftCardCodeMinChars
  }
}
